import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import type { GetStandardDeviationsResponse } from '@/features/standard-deviations/types';
import {
  ANALYSIS_TERMINAL_STATUSES,
  AnalysisStatus,
  type WorkspaceType,
} from '@/features/workspaces/constants';
import { resourceLatestVersionQuery } from '@/features/workspaces/queries';
import { parseStreamRow, queryStream, type StreamRowPayload } from '@/features/workspaces/stream';

export const STANDARD_DEVIATIONS_QUERY_KEY = 'deviations' as const;

export const standardDeviationsQuery = (type: WorkspaceType, resourceId: string) => {
  const docType = DOCUMENT_TYPE[type];
  const queryKey = [STANDARD_DEVIATIONS_QUERY_KEY, type, resourceId] as const;
  return queryOptions({
    queryKey,
    queryFn: async () =>
      (
        await api.get<GetStandardDeviationsResponse>('v1/analysis/standard_deviations', {
          params: { type: docType, resourceId },
        })
      ).data,
    meta: {
      stream: queryStream<GetStandardDeviationsResponse, StreamRowPayload<AnalysisStatus>>({
        topic: 'standard_deviation',
        type: docType,
        resourceId,
        versioned: true,
        isActive: (data) =>
          data?.items.some((item) => !ANALYSIS_TERMINAL_STATUSES.includes(item.status)) ?? false,
        onStatusUpdate: (payload, queryClient) => {
          const invalidate = () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries(resourceLatestVersionQuery(resourceId));
          };
          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate();
          if (!row) return;

          const current = queryClient.getQueryData<GetStandardDeviationsResponse>(queryKey);
          const known = current?.items.some((item) => item.id === row.rowId);
          if (!known || ANALYSIS_TERMINAL_STATUSES.includes(row.status)) return invalidate();

          queryClient.setQueryData<GetStandardDeviationsResponse>(
            queryKey,
            (old) =>
              old && {
                ...old,
                items: old.items.map((item) =>
                  item.id === row.rowId
                    ? { ...item, status: row.status, progress: row.progress }
                    : item,
                ),
              },
          );
        },
      }),
    },
  });
};
